import React, { memo, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Sync as SyncIcon,
  CheckCircle as CheckCircleIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import type {
  ResyncAllResponse,
  ResyncChange,
  ResyncChangedField,
} from '../../services/generateQuestionPaperService';

/**
 * Human-friendly labels for the raw field-change keys emitted by the
 * backend. Also used by the chip row inside each change entry.
 */
const FIELD_LABELS: Record<ResyncChangedField, string> = {
  questionText:   'Question text',
  type:           'Type',
  importance:     'Importance',
  options:        'Options',
  correctAnswer:  'Correct answer',
  answer:         'Sample answer',
  answerImages:   'Answer images',
  questionImages: 'Question images',
  subParts:       'Sub-parts',
};

interface SyncPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  onApply: () => void;
  /** Paper title — shown in the subtitle so users know what they're syncing. */
  paperTitle: string;
  /** Preview data from the dry-run. Null while loading. */
  preview: ResyncAllResponse | null;
  /** Dry-run is in flight. */
  loading: boolean;
  /** Real apply call is in flight. */
  applying: boolean;
  /** Dry-run failed — shown as an inline Alert. */
  error?: string | null;
}

/**
 * Preview dialog shown before a paper-level bulk sync is applied.
 *
 * Three content modes:
 *   • loading  — centered spinner + label while the dry-run is in flight
 *   • error    — Alert with retry affordance (via Cancel-then-reopen)
 *   • preview  — counts summary at the top, scrollable list of per-row
 *                changes underneath (section + Q-number, which fields
 *                differ, before/after text snippet when relevant)
 *
 * The Apply button is disabled while loading or applying. On apply, the
 * list stays visible with the button flipped to a spinner — authors see
 * exactly what they're committing until the server comes back.
 */
const SyncPreviewDialog = memo<SyncPreviewDialogProps>(({
  open,
  onClose,
  onApply,
  paperTitle,
  preview,
  loading,
  applying,
  error,
}) => {
  const updated   = preview?.updated   ?? 0;
  const unchanged = preview?.unchanged ?? 0;
  const dangling  = preview?.dangling  ?? 0;
  const changes   = preview?.changes   ?? [];

  // Count breakdown chips — rendered only when preview data exists. All
  // three are shown even when zero so the user sees the full picture
  // (e.g. "0 to update, 42 in sync" tells them nothing changed on bank).
  const countChips = useMemo(() => (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      <Chip
        icon={<SyncIcon sx={{ fontSize: 16 }} />}
        label={`${updated} to update`}
        size="small"
        color={updated > 0 ? 'primary' : 'default'}
        variant={updated > 0 ? 'filled' : 'outlined'}
        sx={{ fontWeight: 600 }}
      />
      <Chip
        icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
        label={`${unchanged} already in sync`}
        size="small"
        color={unchanged > 0 ? 'success' : 'default'}
        variant="outlined"
      />
      {dangling > 0 && (
        <Chip
          icon={<WarningIcon sx={{ fontSize: 16 }} />}
          label={`${dangling} source removed`}
          size="small"
          color="warning"
          variant="outlined"
        />
      )}
    </Box>
  ), [updated, unchanged, dangling]);

  const canApply = !loading && !applying && updated > 0;
  const closeOnCancel = () => {
    if (applying) return; // ignore while mid-apply
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={closeOnCancel}
      maxWidth="md"
      fullWidth
      // Full-width title row with a close affordance — matches the pattern
      // used in the existing paper-builder dialogs.
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              Sync from Question Bank
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {paperTitle}
            </Typography>
          </Box>
          <Tooltip title="Close">
            <span>
              <IconButton
                size="small"
                onClick={closeOnCancel}
                disabled={applying}
                sx={{ mt: -0.5, mr: -0.5 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {loading && (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            py: 7,
            px: 3,
          }}>
            <CircularProgress size={32} thickness={4} />
            <Typography variant="body2" color="text.secondary">
              Checking the question bank for updates…
            </Typography>
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error" variant="outlined">
              {error}
            </Alert>
          </Box>
        )}

        {!loading && !error && preview && (
          <>
            {/* Summary strip — sticky counts + a one-liner that frames
                the apply action. Kept out of the scrollable list so the
                author always has the context visible. */}
            <Box sx={{ p: 3, bgcolor: 'action.hover' }}>
              {countChips}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                {updated === 0
                  ? 'Everything is already up to date. Nothing will be overwritten.'
                  : `Applying will overwrite the snapshot on ${updated} question${updated === 1 ? '' : 's'} with the latest bank version. Local edits to those questions will be lost. Marks, order, and group settings are not affected.`}
              </Typography>
            </Box>

            {updated > 0 && (
              <>
                <Divider />
                {/* Scrollable list — full list rendered, not truncated.
                    The whole point of the preview is to let authors
                    actually read what's changing. */}
                <Box sx={{
                  maxHeight: 480,
                  overflowY: 'auto',
                  px: 3,
                  py: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}>
                  {changes.map((c, idx) => (
                    <PreviewRow key={c.paperQuestionId} change={c} isLast={idx === changes.length - 1} />
                  ))}
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={closeOnCancel} disabled={applying} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onApply}
          disabled={!canApply}
          startIcon={applying ? <CircularProgress size={16} thickness={5} color="inherit" /> : <SyncIcon />}
        >
          {applying
            ? 'Applying…'
            : updated > 0
              ? `Apply updates to ${updated} question${updated === 1 ? '' : 's'}`
              : 'Apply updates'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

SyncPreviewDialog.displayName = 'SyncPreviewDialog';

export default SyncPreviewDialog;

// ───── Preview row ─────────────────────────────────────────────────────

interface PreviewRowProps {
  change: ResyncChange;
  isLast: boolean;
}

/**
 * One question's worth of change info. Section + Q-number header, the
 * list of fields that will be overwritten as outlined chips, and —
 * when the question text itself is one of the changed fields — a
 * compact "Was / Now" diff. For changes that don't touch the question
 * text, we show the current text once as context so the author can
 * identify the question.
 */
const PreviewRow: React.FC<PreviewRowProps> = memo(({ change, isLast }) => {
  const textChanged = change.fieldsChanged.includes('questionText');

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        '&:hover': {
          borderColor: 'primary.light',
          bgcolor: 'action.hover',
        },
        transition: 'border-color 0.15s, background-color 0.15s',
        mb: isLast ? 0 : 0,
      }}
    >
      <Box sx={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 2,
        mb: 1,
        flexWrap: 'wrap',
      }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {change.sectionName}
          <Box component="span" sx={{ mx: 0.75, color: 'text.disabled' }}>•</Box>
          <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            Q{change.order + 1}
          </Box>
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {change.fieldsChanged.map(f => (
            <Chip
              key={f}
              label={FIELD_LABELS[f] || f}
              size="small"
              variant="outlined"
              sx={{
                height: 22,
                fontSize: '0.7rem',
                borderColor: 'primary.light',
                color: 'primary.dark',
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          ))}
        </Box>
      </Box>

      {textChanged ? (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          columnGap: 1,
          rowGap: 0.5,
          mt: 1,
          fontSize: '0.82rem',
          lineHeight: 1.5,
        }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.disabled', pt: '2px' }}>
            Was
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              textDecoration: 'line-through',
              textDecorationColor: 'rgba(0,0,0,0.25)',
            }}
          >
            {change.currentText || <em>(empty)</em>}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.dark', pt: '2px' }}>
            Now
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            {change.newText || <em>(empty)</em>}
          </Typography>
        </Box>
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, fontStyle: 'italic' }}
        >
          {change.currentText || <em>(no question text)</em>}
        </Typography>
      )}
    </Box>
  );
});

PreviewRow.displayName = 'SyncPreviewDialog.PreviewRow';
